import React, {useCallback} from 'react'
import {useForm} from 'react-hook-form'
import {set} from 'lodash'
import {isEmpty} from 'ramda'
import ComponentConfig, {
  ComponentConfigProps,
  RemoveButton,
  SaveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {CountDownTimerSettings} from 'Event/Dashboard/components/CountDownTimer'
import {REMOVE, useSaveTemplate} from 'Event/TemplateUpdateProvider'
import StylingPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/StylingPanel'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import {useAutoUpdate} from 'organization/Event/TemplateEditor'
import {useAddTranslation, useRemoveTranslations} from 'Event/LanguageProvider'
import Settings from 'Event/Dashboard/components/CountDownTimer/CountDownTimerConfig/Settings'
import Styling from 'Event/Dashboard/components/CountDownTimer/CountDownTimerConfig/Styling'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'

export const buildLabelPlaceholders = (id: string) => ({
  days: `{{countdown_timer_items_${id}_labels_days}}`,
  hours: `{{countdown_timer_items_${id}_labels_hours}}`,
  minutes: `{{countdown_timer_items_${id}_labels_minutes}}`,
  seconds: `{{countdown_timer_items_${id}_labels_seconds}}`,
  description: `{{countdown_timer_items_${id}_labels_description}}`,
  separator: `{{countdown_timer_items_${id}_labels_separator}}`,
})

export default function CountDownTimerConfig(
  props: ComponentConfigProps & {
    id: string
    keyPath: string
    countDownTimer: CountDownTimerSettings
  },
) {
  const {showing, onClose, id, keyPath, countDownTimer} = props
  const {control, handleSubmit, watch, formState} = useForm()
  const addTranslation = useAddTranslation()
  const updateTemplate = useSaveTemplate()
  const removeTranslations = useRemoveTranslations()

  const save = (data: CountDownTimerSettings) => {
    const updated = {
      ...data,
      labels: buildLabelPlaceholders(id),
    }

    updateTemplate({
      [keyPath]: updated,
      localization: addTranslation({
        [`countdown_timer_items_${id}_labels_days`]: data.labels.days || '',
        [`countdown_timer_items_${id}_labels_hours`]: data.labels.hours,
        [`countdown_timer_items_${id}_labels_minutes`]: data.labels.minutes,
        [`countdown_timer_items_${id}_labels_seconds`]: data.labels.seconds,
        [`countdown_timer_items_${id}_labels_description`]: data.labels
          .description,
        [`countdown_timer_items_${id}_labels_separator`]: data.labels.separator,
      }),
    })

    onClose()
  }

  const remove = useCallback(() => {
    updateTemplate({
      [keyPath]: REMOVE,
      localization: removeTranslations([
        `countdown_timer_items_${id}_labels_description`,
        `countdown_timer_items_${id}_labels_days`,
        `countdown_timer_items_${id}_labels_hours`,
        `countdown_timer_items_${id}_labels_minutes`,
        `countdown_timer_items_${id}_labels_seconds`,
        `countdown_timer_items_${id}_labels_separator`,
      ]),
    })
    onClose()
  }, [updateTemplate, removeTranslations, keyPath, id, onClose])

  useAutoUpdate({
    values: set({}, keyPath, watch()),
    when: !isEmpty(watch()),
  })

  return (
    <ComponentConfig
      showing={showing}
      onClose={onClose}
      title="Count Down Timer Config"
      onSubmit={handleSubmit(save)}
      hasChanges={formState.isDirty}
    >
      <SettingsPanel>
        <Settings control={control} countDownTimer={countDownTimer} />
      </SettingsPanel>
      <StylingPanel>
        <Styling control={control} countDownTimer={countDownTimer} />
      </StylingPanel>
      <RulesPanel rules={countDownTimer.rules} control={control} name="rules" />
      <Footer>
        <SaveButton type="submit" />
        <RemoveButton aria-label="remove countdown" onClick={remove} showing />
      </Footer>
    </ComponentConfig>
  )
}
